import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

const FAQSection = styled('div', {
  name: 'faq',
  slot: 'Root'
})(({ theme }) => ({
  marginBottom: '80px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '64px'
  }
}));

const FAQItem = styled('div', {
  name: 'Item'
})(({ theme }) => ({
  marginTop: '16px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 4px 16px 2px rgba(0, 0, 0, 0.12)',
  padding: '24px',
  borderRadius: '16px',
  [theme.breakpoints.up('md')]: {
    marginTop: '36px'
  }
}));

const FaqDivider = styled('div', {
  name: 'FaqDivider'
})(({ theme }) => ({
  border: 'none',
  borderBottom: `2px solid ${theme.palette.border.main}`,
  margin: '10px 0'
}));

const TitleDivider = styled(Divider, {
  name: 'TitleDivider'
})(({ theme }) => ({
  marginTop: '8px',
  [theme.breakpoints.up('md')]: {
    marginTop: '16px'
  }
}));

const HeadingStyled = styled(Heading, {
  name: 'TitleDivider'
})(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    fontSize: '32px',
    lineHeight: '40px',
    fontWeight: 700
  }
}));

const LinkStyled = styled('a', {
  name: 'LinkStyled'
})(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.text.blue
}));

const Faq = () => (
  <FAQSection>
    <Box mb={{ xs: 0, md: '8px' }}>
      <HeadingStyled
        aria-label="subheader"
        color="primary"
        family="secondary"
        size="3xl"
        weight="medium"
      >
        Frequently Asked Questions
      </HeadingStyled>
      <TitleDivider />
    </Box>
    <FAQItem>
      <Text
        aria-label="subheader"
        color="primary"
        component="p"
        size="xl"
        weight="medium"
      >
        Is there a minimum balance to open an account?
      </Text>
      <FaqDivider />
      <Text
        aria-label="subheader"
        color="primary"
        size="lg"
      >
        No, there is no minimum opening deposit or minimum balance required with a ZenBusiness
        Banking account. So there’s no additional cost to having the account ready for when you need
        it.
      </Text>
    </FAQItem>
    <FAQItem>
      <Text
        aria-label="subheader"
        color="primary"
        component="p"
        size="xl"
        weight="medium"
      >
        Can both the physical and virtual debit cards be used to pay online?
      </Text>
      <FaqDivider />
      <Text
        aria-label="subheader"
        color="primary"
        size="lg"
      >
        You can use both your physical and virtual cards for online purchases. However, we recommend
        that for all e-commerce purchases you use your virtual card details (which are easy to see
        in your ZenBusiness Banking Dashboard). And for in-person purchases, use your physical debit
        card. In the event of an issue, it’s a bit faster to replace your virtual card than your
        physical card (which has to be mailed).
      </Text>
    </FAQItem>
    <FAQItem>
      <Text
        aria-label="subheader"
        color="primary"
        component="p"
        size="xl"
        weight="medium"
      >
        Can any business owner apply?
      </Text>
      <FaqDivider />
      <Text
        aria-label="subheader"
        color="primary"
        size="lg"
      >
        We’re anxious to offer ZenBusiness Banking to everyone, but it’s only available to those who
        form their business through ZenBusiness at the current time. It’s also only available to
        customers who are structured as a single-member LLC (among a few additional criteria).
      </Text>
    </FAQItem>
    <FAQItem>
      <Text
        aria-label="subheader"
        color="primary"
        component="p"
        size="xl"
        weight="medium"
      >
        Are my deposits insured by the FDIC?
      </Text>
      <FaqDivider />
      <Stack spacing="32px">
        <Text
          aria-label="subheader"
          color="primary"
          size="lg"
        >
          Yes. Your deposits are insured up to the FDIC standard insurance amount. This insurance is
          provided through our partner bank, Thread Bank; Member FDIC. Their FDIC certificate number
          is [9499] and can be found on the FDIC BankFind Tool.
        </Text>
        <Text
          aria-label="subheader"
          color="primary"
          size="lg"
        >
          Your deposits qualify for up to $3,000,000 in FDIC insurance coverage when placed at
          program banks in the Thread Bank deposit sweep program. Your deposits at each program bank
          become eligible for FDIC insurance up to $250,000, inclusive of any other deposits you may
          already hold at the bank in the same ownership capacity. You can access the terms and
          conditions of the sweep program at{' '}
          <LinkStyled href="https://go.thread.bank/sweepdisclosure">
            https://go.thread.bank/sweepdisclosure
          </LinkStyled>{' '}
          and a list of program banks at{' '}
          <LinkStyled href="https://go.thread.bank/programbanks">
            https://go.thread.bank/programbanks
          </LinkStyled>
          . Please contact customerservice@thread.bank with questions regarding the sweep program.
        </Text>
      </Stack>
    </FAQItem>
    <FAQItem>
      <Text
        aria-label="subheader"
        color="primary"
        component="p"
        size="xl"
        weight="medium"
      >
        Can I deposit cash into my ZenBusiness Banking account?
      </Text>
      <FaqDivider />
      <Text
        aria-label="subheader"
        color="primary"
        size="lg"
      >
        The only way to currently get cash into your ZenBusiness Banking account is to transfer it
        in from another account that accepts cash deposits.
      </Text>
    </FAQItem>
  </FAQSection>
);

export default Faq;
